import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import LanguageSelect from './LanguageSelect';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import Grid from '@material-ui/core/Grid';

import logoWhite from './imges/logo-white.svg';
import FooterAccordion from './FooterAccordion';
import Typography from '@material-ui/core/Typography';
import FooterLinkList from './FooterLinkList';
import FooterLink from './FooterLink';
import LinkedIn from './SvgIcons/LinkedIn';
import IconLink from './IconLink';
import YouTube from './SvgIcons/YouTube';
import Facebook from './SvgIcons/Facebook';
import Instagram from './SvgIcons/Instagram';
import { useSelector } from 'react-redux';

const useStyles = makeStyles((theme) => ({
  root: {
    background: '#202f31',
    color: 'white',
    position: 'relative',
    marginTop: 12,
  },
  languageSelect: {},
  footerContainer: {
    maxWidth: 1434,
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingLeft: 24,
    paddingRight: 24,
    [theme.breakpoints.up('sm')]: {
      paddingLeft: 32,
      paddingRight: 32,
    },
    [theme.breakpoints.up('md')]: {
      paddingLeft: 40,
      paddingRight: 40,
    },
    [theme.breakpoints.up('lg')]: {
      paddingLeft: 64,
      paddingRight: 64,
    },
    [theme.breakpoints.up('xl')]: {
      paddingLeft: 77,
      paddingRight: 77,
    },
  },
  footerTop: {
    padding: '3.75rem 0 2.375rem',
  },
  footerMain: {
    borderTop: '1px solid hsla(0,0%,100%,.1)',
    borderBottom: '1px solid hsla(0,0%,100%,.1)',
    '& ul': {
      margin: 0,
      padding: 0,
      listStyleType: 'none',
      lineHeight: '1.6667',
      fontSize: '1rem',
      '& li a': {
        color: 'hsla(0,0%,100%,.8)',
        transition: 'color .2s ease-in-out',
        '&:hover': {
          color: 'white',
        },
      },
    },
    '& p': {
      color: 'hsla(0,0%,100%,.8)',
    },
    '& $footerContainer > div': {
      [theme.breakpoints.up('md')]: {
        paddingTop: '2.375rem',
        paddingBottom: '2.375rem',
      },
      '&:first-child': {
        [theme.breakpoints.up('md')]: {
          borderRight: '1px solid hsla(0,0%,100%,.1)',
        },
      },
    },
    '& $footerContainerRow $footerContainer > div+div': {
      paddingTop: '1.375rem',
      paddingBottom: '1.375rem',
      [theme.breakpoints.up('md')]: {
        paddingTop: '1.375rem',
        paddingBottom: '1.375rem',
      },
    },
  },
  footerMainContainer: {
    '& > div': {
      [theme.breakpoints.up('md')]: {
        paddingLeft: 12,
        paddingRight: 12,
      },
    },
    '& $footerMainContainer': {
      [theme.breakpoints.up('md')]: {
        marginLeft: -12,
        marginRight: -12,
      },
    },
  },
  footerContainerRow: {
    borderTop: '1px solid hsla(0,0%,100%,.1)',
  },
  footerBottom: {
    padding: '1.375rem 0',
    fontSize: '.875rem',
    color: 'hsla(0,0%,100%,.5)',
    '& ul': {
      margin: 0,
      padding: 0,
      listStyle: 'none',
      display: 'flex',
      flexWrap: 'wrap',
      '& li': {
        display: 'inline-block',
        verticalAlign: 'middle',
        '& a': {
          color: 'hsla(0,0%,100%,.5)',
          transition: 'color .2s ease-in-out',
          '&:hover': {
            color: 'white',
          },
        },
      },
      '& li + li': {
        marginLeft: '1rem',
        '&:before': {
          content: '"-"',
          display: 'inline-block',
          marginRight: '1rem',
          color: 'currentColor',
        },
      },
    },
    '& $footerContainer': {
      [theme.breakpoints.up('md')]: {
        flexDirection: 'row-reverse',
      },
    },
    '& $languageSelect': {
      marginBottom: '2rem',
      [theme.breakpoints.up('md')]: {
        marginBottom: 0,
      },
    },
  },
  logo: {
    display: 'block',
    width: 126,
    margin: '0 auto',
    [theme.breakpoints.up('md')]: {
      width: 174,
      margin: 0,
    },
  },
  navigationCol: {
    borderBottom: '1px solid hsla(0,0%,100%,.1)',
    [theme.breakpoints.up('md')]: {
      borderBottom: 0,
    },
    '&:first-child': {
      [theme.breakpoints.up('md')]: {
        marginLeft: -12,
        marginRight: -12,
      },
    },
  },
  address: {
    fontStyle: 'normal',
    color: 'white',
    marginBottom: '1rem',
    [theme.breakpoints.up('md')]: {
      marginBottom: 0,
    },
    '& a': {
      color: 'inherit',
      transition: 'color .2s ease-in-out',
      '&:hover': {
        color: 'white',
        textDecoration: 'none',
      },
    },
    '& p': {
      margin: 0,
      marginBottom: '1rem',
    },
  },
  socialMediaLinks: {
    '& a': {
      display: 'inline-flex',
    },
    '& a + a': {
      marginLeft: '.375rem',
    },
  },
  socialMediaText: {
    padding: '0 12px 12px',
    fontSize: '0.8rem',
  },
  footerBottomLinks: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    '& > p': {
      marginRight: '4.125rem',
    },
  },
}));

export const Footer = ({ className }) => {
  const classes = useStyles();

  const { selectedCountry, countries } = useSelector((state) => state.Country);

  const [details, setDetails] = useState(null);

  useEffect(() => {
    countries.map((country) => {
      if (country.identifier === selectedCountry) {
        setDetails(country.details);
      }
    });
  }, [selectedCountry, countries]);

  if (!details) {
    return null;
  }

  return (
    <div className={clsx(classes.root, className)}>
      <div className={classes.footerTop}>
        <Grid container classes={{ root: classes.footerContainer }}>
          <img src={logoWhite} alt="Losberger de boer footer logo" className={classes.logo} />
        </Grid>
      </div>
      <div className={classes.footerMain}>
        <Grid container classes={{ root: classes.footerContainer }}>
          <Grid item md={9} xs={12}>
            <Grid container classes={{ root: classes.footerMainContainer }}>
              <Grid item md={6} xs={12} classes={{ root: classes.navigationCol }}>
                <FooterAccordion
                  title={
                    <FooterLink href={details.footer.ourSolutions.ourSolutionsLink}>
                      {details.footer.ourSolutions.ourSolutionsText}
                    </FooterLink>
                  }
                  staticUp={'md'}
                >
                  <Grid container classes={{ root: classes.footerMainContainer }}>
                    <Grid item md={6} xs={12}>
                      <FooterLinkList
                        items={[
                          {
                            link: details.footer.ourSolutions.buyEventSpaceLink,
                            text: details.footer.ourSolutions.buyEventSpaceText,
                          },
                          {
                            link: details.footer.ourSolutions.rentEventSpaceLink,
                            text: details.footer.ourSolutions.rentEventSpaceText,
                          },
                          {
                            link: details.footer.ourSolutions.commercialSpaceLink,
                            text: details.footer.ourSolutions.commercialSpaceText,
                          },
                          {
                            link: details.footer.ourSolutions.rapidDeploymentSpaceLink,
                            text: details.footer.ourSolutions.rapidDeploymentSpaceText,
                          },
                        ]}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <FooterLinkList
                        items={[
                          {
                            link: details.footer.ourSolutions.commercialIndustriesLink,
                            text: details.footer.ourSolutions.commercialIndustriesText,
                          },
                          {
                            link: details.footer.ourSolutions.sportEventsLink,
                            text: details.footer.ourSolutions.sportEventsText,
                          },
                          {
                            link: details.footer.ourSolutions.covidTestSitesLink,
                            text: details.footer.ourSolutions.covidTestSitesText,
                          },
                          {
                            link: details.footer.ourSolutions.emergencyHousingLink,
                            text: details.footer.ourSolutions.emergencyHousingText,
                          },
                        ]}
                      />
                    </Grid>
                  </Grid>
                </FooterAccordion>
              </Grid>
              <Grid item md={3} xs={12} classes={{ root: classes.navigationCol }}>
                <FooterAccordion
                  title={
                    <FooterLink href={details.footer.productsServices.productsServicesLink}>
                      {details.footer.productsServices.productsServicesText}
                    </FooterLink>
                  }
                  staticUp={'md'}
                >
                  <FooterLinkList
                    items={[
                      {
                        link: details.footer.productsServices.eventSpaceProductsLink,
                        text: details.footer.productsServices.eventSpaceProductsText,
                      },
                      {
                        link: details.footer.productsServices.commercialSpaceProductsLink,
                        text: details.footer.productsServices.commercialSpaceProductsText,
                      },
                      {
                        link: details.footer.productsServices.deploymentSpaceProductsLink,
                        text: details.footer.productsServices.deploymentSpaceProductsText,
                      },
                      {
                        link: details.footer.productsServices.inspiringCasesLink,
                        text: details.footer.productsServices.inspiringCasesText,
                      },
                    ]}
                  />
                </FooterAccordion>
              </Grid>
              <Grid item md={3} xs={12} classes={{ root: classes.navigationCol }}>
                <FooterAccordion title={details.footer.ourExpertise.ourExpertiseText} staticUp={'md'}>
                  <FooterLinkList
                    items={[
                      {
                        link: details.footer.ourExpertise.whyLdbLink,
                        text: details.footer.ourExpertise.whyLdbText,
                      },
                      {
                        link: details.footer.ourExpertise.aboutUsLink,
                        text: details.footer.ourExpertise.aboutUsText,
                      },
                      {
                        link: details.footer.ourExpertise.professionalInsightsLink,
                        text: details.footer.ourExpertise.professionalInsightsText,
                      },
                      {
                        link: details.footer.ourExpertise.newsOverviewLink,
                        text: details.footer.ourExpertise.newsOverviewText,
                      },
                    ]}
                  />
                </FooterAccordion>
              </Grid>
            </Grid>
          </Grid>
          <Grid item md={3} xs={12} classes={{ root: classes.footerMainContainer }}>
            <FooterAccordion title={details.footer.contactDetails.contactDetailsText} staticUp={'md'}>
              <Typography component={'address'} className={classes.address}>
                <p
                  dangerouslySetInnerHTML={{
                    __html: details.footer.contactDetails.address,
                  }}
                />
                <FooterLink
                  href={details.footer.contactDetails.allContactsLink}
                  text={details.footer.contactDetails.allContactsText}
                />
              </Typography>
            </FooterAccordion>
          </Grid>
        </Grid>
        <div className={classes.footerContainerRow}>
          <Grid container classes={{ root: classes.footerContainer }}>
            <Grid item md={9} xs={12} />
            <Grid item md={3} xs={12} classes={{ root: classes.footerMainContainer }}>
              {details.footer.socialMedia.engageWithUs && (
                <Typography className={classes.socialMediaText}>
                  {details.footer.socialMedia.engageWithUs}
                </Typography>
              )}
              <div className={classes.socialMediaLinks}>
                <IconLink href={details.footer.socialMedia.linkedIn}>
                  <LinkedIn />
                </IconLink>
                <IconLink href={details.footer.socialMedia.youtube}>
                  <YouTube />
                </IconLink>
                <IconLink href={details.footer.socialMedia.facebook}>
                  <Facebook />
                </IconLink>
                <IconLink href={details.footer.socialMedia.instagram}>
                  <Instagram />
                </IconLink>
              </div>
            </Grid>
          </Grid>
        </div>
      </div>
      <div className={classes.footerBottom}>
        <Grid container classes={{ root: classes.footerContainer }}>
          <Grid item md={3} xs={12}>
            <LanguageSelect className={classes.languageSelect} fullWidth />
          </Grid>
          <Grid item md={9} className={classes.footerBottomLinks}>
            <Typography>{details.footer.copyrightMark}</Typography>
            <FooterLinkList
              items={[
                {
                  link: details.footer.privacyDisclaimerLink,
                  text: details.footer.privacyDisclaimerText,
                },
                {
                  link: details.footer.imprintLink,
                  text: details.footer.imprintText,
                },
                {
                  link: details.footer.pressLink,
                  text: details.footer.pressText,
                },
                {
                  link: details.footer.downloadsLink,
                  text: details.footer.downloadsText,
                },
                {
                  link: details.footer.copyrightLink,
                  text: details.footer.copyrightText,
                },
              ]}
            />
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

Footer.propTypes = {
  className: PropTypes.string,
};

export default Footer;
